<template>
  <div class="traffic-fields">
    <SettingsField :label="$t('platform.arbitrage.accept')" :hint="$t('platform.card.fields.tb_accept')">
      <div class="traffic-fields__radios">
        <div v-for="option in acceptOptions" :key="option.value" class="radio" role="radio" :aria-checked="form.tb_subscription === option.value" @click="form.tb_subscription = option.value">
          <input type="radio" :checked="form.tb_subscription === option.value" :name="`tb-accept-${uid}`" />
          <span class="radio__visible" :class="{ active: form.tb_subscription === option.value }"></span>
          <span class="traffic-fields__radio-text">
            {{ $t(option.title) }}
            <span v-if="option.tooltip" class="traffic-fields__tooltip" v-tooltip="{ content: $t(option.tooltip), triggers: ['hover', 'click'] }">
              <svg v-svg symbol="info" size="0 0 18 18" role="info"></svg>
            </span>
          </span>
        </div>
      </div>
      <TraffbackWithMacros v-if="form.tb_subscription === 'user'" v-model="form.tb_subscription_url" :macroses="tbMacroses" :invalid="acceptUrlInvalid" :title="'tb_subscription_url'" @addMacros="addMacros" />
    </SettingsField>

    <SettingsField :label="$t('platform.arbitrage.reject')" :hint="$t('platform.card.fields.tb_reject')">
      <div class="traffic-fields__radios">
        <div v-for="option in rejectOptions" :key="option.value" class="radio" role="radio" :aria-checked="form.tb_complexity === option.value" @click="form.tb_complexity = option.value">
          <input type="radio" :checked="form.tb_complexity === option.value" :name="`tb-reject-${uid}`" />
          <span class="radio__visible" :class="{ active: form.tb_complexity === option.value }"></span>
          <span class="traffic-fields__radio-text">{{ $t(option.title) }}</span>
        </div>
      </div>
      <TraffbackWithMacros v-if="form.tb_complexity === 'user'" v-model="form.tb_complexity_url" :macroses="tbMacroses" :invalid="rejectUrlInvalid" :title="'tb_complexity_url'" @addMacros="addMacros" />
    </SettingsField>

    <SettingsField :label="$t('platform.arbitrage.redirect_type')" :hint="$t('platform.card.fields.redirect_type')">
      <custom-multiselect v-model="form.redirect_type" :list="redirectModes" class="payment" :allowEmpty="false">
        <template v-slot:block="props">
          <span>{{ $t(props.value.title) }}</span>
        </template>
        <template v-slot:list="props">
          <span>{{ $t(props.value.title) }}</span>
        </template>
      </custom-multiselect>
    </SettingsField>

    <SettingsField :label="$t('platform.arbitrage.difficult')" :hint="$t('platform.card.fields.complexity')" :tooltip="$t('platform.difficult_pop_up')">
      <custom-multiselect v-model="form.complexity" :list="[1, 2, 3, 4, 5]" class="traffic-fields__narrow" :allowEmpty="false">
        <template v-slot:block="props">
          <span>{{ props.value }}</span>
        </template>
        <template v-slot:list="props">
          <span>{{ props.value }}</span>
        </template>
      </custom-multiselect>
    </SettingsField>

    <SettingsField :label="$t('platform.card.fields.extra_formats_title')" :hint="$t('platform.card.fields.extra_formats')">
      <div>
        <CheckBoxWrapper v-model="form.enable_in_page_ads" :text="$t('platform.site.turn_inpage_ad')" />
      </div>
      <div class="mt-3">
        <CheckBoxWrapper v-model="form.enable_onclick_ads" :text="$t('platform.site.turn_onclick_ad')" />
      </div>
    </SettingsField>

    <SettingsField :label="$t('platform.arbitrage.fullsize_mode')" :hint="$t('platform.card.fields.fullscreen')" :disabled="form.enable_onclick_ads">
      <CheckBoxWrapper v-model="form.fullscreen" :disabled="form.enable_onclick_ads" :text="$t(form.fullscreen ? 'platform.card.on' : 'platform.card.off')" />
      <p v-if="form.enable_onclick_ads" class="traffic-fields__warn">
        <svg v-svg symbol="info" size="0 0 17 17" role="info"></svg>
        {{ $t("platform.arbitrage.not_allowed_fullsize_mode") }}
      </p>
    </SettingsField>

    <SettingsField :label="$t('platform.arbitrage.send_to_current_domain')" :hint="$t('platform.card.fields.actual_domain')">
      <CheckBoxWrapper v-model="form.redirect_to_actual_domain" :text="$t(form.redirect_to_actual_domain ? 'platform.card.on' : 'platform.card.off')" />
    </SettingsField>

    <SettingsField v-if="form.redirect_to_actual_domain" :label="$t('platform.arbitrage.domain_strategy')" :hint="$t('platform.card.fields.domain_rotation')">
      <custom-multiselect v-model="form.domain_rotation" :list="rotationModes" class="payment" :allowEmpty="false">
        <template v-slot:block="props">
          <span>{{ $t(props.value.title) }}</span>
        </template>
        <template v-slot:list="props">
          <span>{{ $t(props.value.title) }}</span>
          <br />
          <small class="traffic-fields__option-hint">{{ rotationHint(props.value.value) }}</small>
        </template>
      </custom-multiselect>
      <p class="traffic-fields__warn">
        <svg v-svg symbol="info" size="0 0 17 17" role="info"></svg>
        {{ $t("platform.arbitrage.rotation_warning") }}
      </p>
    </SettingsField>
  </div>
</template>
<script>
import SettingsField from "@/components/PlatformCard/SettingsField.vue";
import TraffbackWithMacros from "@/components/TraffbackWithMacros.vue";
import CheckBoxWrapper from "@/components/CheckBoxWrapper.vue";
import addMacros from "@/mixins/addMacros";
import { REDIRECT_MODES, ROTATION_MODES } from "@/components/PlatformCard/subaccountPayload";

let uid = 0;

// What an arbitrage platform does with its traffic: trafficback after the subscription request, the redirect,
// extra ad formats and the domains. Edits `form` in place; `v` is the vuelidate state of the form.
export default {
  name: "TrafficFields",
  components: { SettingsField, TraffbackWithMacros, CheckBoxWrapper },
  mixins: [addMacros],
  props: {
    form: {
      type: Object,
      required: true,
    },
    v: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      uid: ++uid,
      redirectModes: REDIRECT_MODES,
      rotationModes: ROTATION_MODES,
      acceptOptions: [
        { value: "default", title: "platform.keep_complexity", tooltip: "platform.keep_complexity_tooltip" },
        { value: "partners", title: "platform.monetization_ph" },
        { value: "user", title: "platform.send_tb_link" },
      ],
      rejectOptions: [
        { value: "user", title: "platform.send_tb_link" },
        { value: "partners", title: "platform.monetization_ph" },
      ],
    };
  },
  computed: {
    // addMacros edits `platform[title]`
    platform() {
      return this.form;
    },
    acceptUrlInvalid() {
      return Boolean(this.v && this.form.tb_subscription === "user" && (this.v.tb_subscription_url.$error || !this.v.tb_subscription_url.url));
    },
    rejectUrlInvalid() {
      return Boolean(this.v && this.form.tb_complexity === "user" && (this.v.tb_complexity_url.$error || !this.v.tb_complexity_url.url));
    },
  },
  watch: {
    // OnClick ads open a new window, which full screen mode would cover
    "form.enable_onclick_ads"(enabled) {
      if (enabled) this.form.fullscreen = false;
    },
  },
  methods: {
    rotationHint(value) {
      return value === "random" ? this.$t("platform.arbitrage.random_hint") : this.$t("platform.arbitrage.new_to_old_hint");
    },
  },
};
</script>
<style lang="scss" scoped>
.traffic-fields__radios {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 24px;

  .radio {
    margin: 0;
    cursor: pointer;
  }
}

.traffic-fields__radio-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.traffic-fields__tooltip {
  display: inline-flex;

  svg {
    fill: #5183cf;
  }
}

.traffic-fields__narrow {
  max-width: 160px;
}

.traffic-fields__option-hint {
  display: block;
  padding-top: 4px;
  white-space: normal;
  color: #9b9ba4;
}

.traffic-fields__warn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: #626262;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    fill: #626262;
  }
}
</style>
